import { FaPlus, FaMinus } from '@meronex/icons/fa';
import AnimateHeight from 'react-animate-height';
import React, { forwardRef, useRef, useState } from 'react';
import { useMount, useUpdateEffect } from 'ahooks';
import { uniqueId } from 'lodash-es';
import useCombinedRefs from '../../hooks/useCombinedRefs';
import {
  customEvents,
  disableChildrenFocus,
  enableChildrenFocus,
} from '../../utils/dom';
import AccordionElementButton, {
  IAccordionElementButton,
} from './AccordionElementButton';

export type TAccordionElement = {
  allowToggle?: boolean;
  ButtonComponent?: React.FunctionComponent<IAccordionElementButton>;
  children: React.ReactNode;
  className?: string;
  closedTitle?: string;
  contentClassName?: string;
  expanded?: boolean;
  initiallyExpanded?: boolean;
  frmHidden?: boolean;
  id: string;
  fixStyleOverflowVisible?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onToggle?: (event: boolean) => void;
  openTitle?: string;
  title?: string;
  variant?: string;
};

const AccordionElement = (
  {
    allowToggle,
    ButtonComponent = AccordionElementButton,
    children,
    className,
    closedTitle,
    contentClassName,
    expanded,
    id,
    initiallyExpanded,
    frmHidden = false,
    fixStyleOverflowVisible = false,
    onToggle,
    onClick,
    openTitle,
    title,
    variant,
  }: TAccordionElement,
  ref: React.Ref<unknown>,
): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(
    allowToggle === false
      ? true
      : fixStyleOverflowVisible !== undefined
        ? !fixStyleOverflowVisible
        : initiallyExpanded ?? expanded ?? false,
  );

  useMount(() => {
    if (allowToggle !== false && fixStyleOverflowVisible !== undefined) {
      setIsExpanded(initiallyExpanded ?? expanded ?? false);
    }
  });

  useUpdateEffect(() => {
    if (expanded !== undefined) setIsExpanded(expanded);
  }, [expanded]);

  const innerRef = useRef<HTMLElement>(null);
  const assignRef = useCombinedRefs(ref, innerRef);

  useUpdateEffect(() => {
    const handleFocus = (ev: Event) => {
      if (
        !(ev.target as HTMLElement).classList.contains('toggleAccordionElement')
      )
        setIsExpanded(true);
    };
    innerRef.current?.addEventListener(customEvents.focus, handleFocus);
    return () => {
      innerRef.current?.removeEventListener(customEvents.focus, handleFocus);
    };
  }, [innerRef.current]);

  React.useEffect(() => {
    if (innerRef.current)
      if (!isExpanded) {
        disableChildrenFocus(innerRef.current);
      } else {
        enableChildrenFocus(innerRef.current);
      }
  }, [isExpanded]);

  const expandedIcons = React.useMemo(() => {
    if (!isExpanded) {
      return (
        <FaPlus
          sx={{
            ml: '4',
          }}
        />
      );
    }
    return (
      <FaMinus
        sx={{
          ml: '4',
        }}
      />
    );
  }, [isExpanded]);
  const prefix = React.useMemo(() => uniqueId(), []);
  const actualId = `${prefix}_${id}`;

  return (
    <section
      aria-label={title}
      key={`${actualId}__${
        title ?? (isExpanded ? openTitle : closedTitle) ?? ''
      }`}
      ref={assignRef as React.Ref<HTMLInputElement>}
      data-variant={`layout.common.components.accordion.${
        variant ?? 'default'
      }`}
      sx={React.useMemo(
        () => ({
          variant: `layout.common.components.accordion.${variant ?? 'default'}`,
          display: frmHidden ? 'none' : 'block',
        }),
        [frmHidden, variant],
      )}
      className={`${className ?? ''} accordionElement`}
    >
      <ButtonComponent
        expandedIcons={expandedIcons}
        id={actualId}
        isExpanded={isExpanded}
        onClick={React.useCallback<React.MouseEventHandler<HTMLElement>>(
          (ev) => {
            if (allowToggle !== false) {
              if (onClick) onClick(ev);
              if (onToggle) onToggle(!isExpanded);
              else setIsExpanded(!isExpanded);
            }
          },
          [allowToggle, isExpanded, onClick, onToggle],
        )}
        title={title ?? (isExpanded ? openTitle : closedTitle) ?? ''}
      />
      <AnimateHeight
        id={`${actualId}_content`}
        duration={150}
        height={isExpanded ? 'auto' : 0}
        className={`content ${contentClassName ?? ''}`}
        aria-hidden={false}
      >
        {children}
      </AnimateHeight>
    </section>
  );
};

export default React.memo(forwardRef(AccordionElement));
